import { API } from '@apis/API';
import HorizontalBlank from '@pages/components/common/atoms/HorizontalBlank';
import { YellowButton } from '@pages/components/common/atoms/YellowButton';
import { Col, Row } from 'antd';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox';
import DOMPurify from 'dompurify';
import React, { FC, useEffect, useMemo, useState } from 'react';
// import { useHistory } from 'react-router-dom'; // 👈 라우터 필요시 주석 해제
import { axiosInstance } from 'src/axios/useRequest';
import styled from 'styled-components';
import DocumentGogeenInfoModal from './DocumentGogeenInfoModal';
import DocumentSafeInfoModal from './DocumentSafeInfoModal';

interface CheckboxItemInterface {
  value: string;
  required: boolean;
  validMessage?: string;
  label: React.ReactNode;
}

interface CmpTemplate {
  templateTechId: string;
  checkLabel: string | null;
  bodyHtml: string | null;
  mandatoryYn: string | null;
}

export const CheckTerms: FC = () => {
  // const history = useHistory(); 
  const [next, isNext] = useState(false);
  const [gogeenModalVisible, setGogeenModalVisible] = useState(false);
  const [safeModalVisible, setSafeModalVisible] = useState(false);
  const [confirmValue, setConfirmValue] = useState(false);

  const [templates, setTemplates] = useState<CmpTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** * ✅ [핵심 수정 1] 서류안심 모달을 위한 '가짜(Legacy)' 체크 리스트
   * 모달은 '개인정보1' 같은 한글 키를 원하므로, 우리가 동의를 받았다면
   * 강제로 해당 키들을 만들어서 모달에 넣어줘야 합니다.
   */
  const legacyCheckedListForModal = useMemo(() => {
    const list = ['운송약관'];
    
    list.push('개인정보1');
    list.push('개인정보2');
    list.push('개인정보3');
    list.push('개인정보4');

    list.push('마케팅1');
    list.push('마케팅2');

    return list;
  }, []);

  const handleGogeenConfirm = (answer: boolean) => {
    setConfirmValue(answer);
    setGogeenModalVisible(false);
    setTimeout(() => {
        setSafeModalVisible(true);
    }, 100); // 약간의 딜레이를 주어 모달 충돌 방지
  };

  // ✅ 모달 종료 시 페이지 이동 강제
  const handleSafeModalClose = () => {
    setSafeModalVisible(false);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        setError(null);
        const { endpoint } = API.CMP_TEMPLATE_SELECT;
        const res = await axiosInstance.get(endpoint);
        const data = (res.data || []).map((t: CmpTemplate) => ({
          ...t,
          checkLabel: t.checkLabel || '',
          bodyHtml: t.bodyHtml || '',
          mandatoryYn: t.mandatoryYn || 'N',
        }));
        setTemplates(data);
      } catch (e) {
        console.error(e);
        setError('동의 항목을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  const templateMap = useMemo(() => {
    const m = new Map<string, CmpTemplate>();
    templates.forEach((t) => m.set(t.templateTechId, t));
    return m;
  }, [templates]);

  const dynamicCheckboxes: CheckboxItemInterface[] = useMemo(
    () =>
      templates.map((t) => ({
        value: t.templateTechId,
        required: t.mandatoryYn?.toUpperCase() === 'Y',
        validMessage: t.mandatoryYn?.toUpperCase() === 'Y' ? '해당 항목에 동의해주시기 바랍니다.' : undefined,
        label: <TermsLabelFont>{t.checkLabel || '(항목명 없음)'}</TermsLabelFont>,
      })),
    [templates],
  );

  const checkboxDataList: CheckboxItemInterface[] = useMemo(
    () => [
      {
        value: '운송약관',
        required: true,
        validMessage: '회사의 운송약관에 관한 설명을 이해하고, 이에 동의하여 주시기 바랍니다.',
        label: (
          <TermsLabelFont>
            본인은 회사의 <BlueFont onClick={() => window.open('https://mydhl.express.dhl/kr/ko/legal.html')}>운송약관</BlueFont>에 관한 설명을 이해하고, 이에 동의합니다.
          </TermsLabelFont>
        ),
      },
      ...dynamicCheckboxes,
    ],
    [dynamicCheckboxes],
  );

  const requiredCheckboxes = checkboxDataList.filter((item) => item.required && item.value !== '운송약관');
  const optionalCheckboxes = checkboxDataList.filter((item) => !item.required);
  const plainOptions = checkboxDataList.map((item) => item.value);

  const [checkedList, setCheckedList] = useState<string[]>([]);
  const [indeterminate, setIndeterminate] = useState(true);
  const [checkAll, setCheckAll] = useState(false);

  const onChange = ({ target }: CheckboxChangeEvent) => {
    if (target.checked) {
      setCheckedList([...checkedList, target.value]);
    } else {
      setCheckedList(checkedList.filter((item) => item !== target.value));
    }
  };

  useEffect(() => {
    setIndeterminate(!!checkedList.length && checkedList.length < plainOptions.length);
    setCheckAll(checkedList.length === plainOptions.length);
  }, [checkedList, plainOptions]);

  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    setCheckedList(e.target.checked ? plainOptions : []);
    setIndeterminate(false);
    setCheckAll(e.target.checked);
  };

  const renderCheckedItem = (item: CheckboxItemInterface) => {
    const isChecked = checkedList.includes(item.value);
    const template = templateMap.get(item.value);
    
    return (
      item && (
        <div key={item.value}>
          <Checkbox onChange={onChange} checked={isChecked} value={item.value}>
            {item.label}
            {next && item.required && !isChecked && <RedFont>{item.validMessage}</RedFont>}
          </Checkbox>
          {template?.bodyHtml && (
            <TermsDiv>
              <HtmlBox dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(template.bodyHtml) }} />
            </TermsDiv>
          )}
        </div>
      )
    );
  };

  const handleNext = (e: any) => {
    if (e) {
        e.preventDefault();
        e.stopPropagation(); 
    }

    isNext(true);
    const requiredList = checkboxDataList.filter((item) => item.required);
    const isValid = requiredList.every((item) => checkedList.includes(item.value));
    
    if (isValid) {
      // 검증 통과 시 바로 모달 오픈
      setGogeenModalVisible(true);
    } else {
        console.log("필수 항목 동의 필요");
    }
  };

  if (loading) return <div>동의 항목을 불러오는 중입니다...</div>;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div>
      <Col>
        <RowItem>
          <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange} checked={checkAll}>
            <TermsLabelFont>본인은 하기 모든 항목에 대하여 동의합니다.</TermsLabelFont>
          </Checkbox>
        </RowItem>
        <RowItem>{renderCheckedItem(checkboxDataList[0])}</RowItem>
        <RowItem>
          <CommonFont>1. 개인정보의 수집 및 이용</CommonFont>
          <br />
          {requiredCheckboxes.map((item) => renderCheckedItem(item))}
        </RowItem>
        {optionalCheckboxes.length > 0 && (
          <RowItem>
            <CommonFont>2. 마케팅 정보 수신 동의(선택적 항목)</CommonFont>
            <br />
            {optionalCheckboxes.map((item) => renderCheckedItem(item))}
          </RowItem>
        )}
      </Col>

      <Row style={{ paddingBottom: 20 }} justify={'center'}>
        <div onClick={handleNext} style={{ display: 'inline-block' }}>
            <YellowButton>다음</YellowButton>
        </div>
      </Row>
      <HorizontalBlank height={15} />

      <DocumentGogeenInfoModal
        visible={gogeenModalVisible}
        onClose={() => setGogeenModalVisible(false)}
        onConfirm={handleGogeenConfirm}
      />
      
      <DocumentSafeInfoModal
        visible={safeModalVisible}
        onClose={handleSafeModalClose}
        checkedList={legacyCheckedListForModal} 
        confirmValue={confirmValue}
      />
    </div>
  );
};

const TermsDiv = styled.div`
  width: 100%;
  @media only screen and (max-width: 767px) {
    overflow-x: auto;
  }
`;
const HtmlBox = styled.div`
  width: 100%;
  @media only screen and (max-width: 767px) {
    & > div, div[style*="margin"] {
      margin: 10px 0 !important;
      width: 100% !important;
      padding: 10px 5px !important;
      box-shadow: none !important;
      box-sizing: border-box !important;
    }
    table {
      width: 100% !important;
      margin: 0 !important;
      table-layout: fixed !important;
    }
    th, td, span, p, div, a {
      font-size: 12px !important;
      line-height: 1.4 !important;
      word-break: break-all !important;
      white-space: normal !important;
    }
    th, td { padding: 4px 2px !important; }
    div[style*="overflow-x"] { display: block !important; overflow-x: auto !important; }
  }
`;
const BlueFont = styled.span` color: blue; `;
const CommonFont = styled.span`
  color: rgb(60, 72, 88);
  font-size: 16px;
  @media only screen and (max-width: 767px) { font-size: 12px !important; }
`;
const TermsLabelFont = styled.span`
  font-size: 16px;
  @media only screen and (max-width: 767px) { font-size: 12px !important; }
  color: black;
`;
const RowItem = styled.div`
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
`;
const RedFont = styled.div` color: red; `;

export default CheckTerms;